/**
 * Receipt files: what can be uploaded, and the upload itself (an upload slot from
 * the service, then a direct POST to storage). Must match ALLOWED_TYPES and
 * MAX_BYTES in backend/service_expense_reports/receipts.py.
 */
import expenseReportsService from '../../services/expenseReportsService';

export const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
export const MAX_RECEIPTS = 5;

const TYPE_BY_EXTENSION = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};
const ALLOWED_TYPES = new Set(Object.values(TYPE_BY_EXTENSION));

// Browsers can't display HEIC/HEIF (iPhone photos), so those are download-only.
const PREVIEWABLE = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp']);

export const ACCEPT = [
  ...Object.keys(TYPE_BY_EXTENSION).map((ext) => `.${ext}`),
  ...ALLOWED_TYPES,
].join(',');

export const ALLOWED_DESCRIPTION = 'PDF, JPEG, PNG, WebP or HEIC, up to 10 MB each';

/** The file's content type, from the browser or (Windows often reports HEIC as '') its extension. */
export const contentTypeOf = (file) => {
  const reported = String(file?.type || '').toLowerCase().replace('image/jpg', 'image/jpeg');
  if (ALLOWED_TYPES.has(reported)) return reported;
  const ext = String(file?.name || '').split('.').pop().toLowerCase();
  return TYPE_BY_EXTENSION[ext] || reported;
};

/** Why a file can't be a receipt, or null if it can. */
export const validateReceiptFile = (file) => {
  if (!ALLOWED_TYPES.has(contentTypeOf(file))) return `${file.name} isn't a supported file. Use a ${ALLOWED_DESCRIPTION}.`;
  if (!file.size) return `${file.name} is empty.`;
  if (file.size > MAX_RECEIPT_BYTES) return `${file.name} is larger than 10 MB.`;
  return null;
};

export const canPreview = (contentType) => PREVIEWABLE.has(contentType);

export const fmtBytes = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

/** Upload one file; resolves to its upload_id. onProgress gets 0..1. */
export const uploadReceipt = async (file, onProgress) => {
  const slot = await expenseReportsService.requestReceiptUpload({
    filename: file.name,
    contentType: contentTypeOf(file),
    size: file.size,
  });
  await expenseReportsService.postReceiptFile(slot, file, onProgress);
  return slot.upload_id;
};

/**
 * Open a receipt link in a new tab. The tab is opened before the link is
 * fetched, so a popup blocker still sees it as the user's click.
 */
export const openReceipt = async (getLink) => {
  const tab = window.open('', '_blank');
  try {
    const link = await getLink();
    if (tab) tab.location.href = link.url;
    else window.location.assign(link.url);
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
};
